import { z } from "zod";
import { Label } from "components/label";
import { Controller, useForm } from "react-hook-form";
import { HttpError, useOne } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { SwitchButton } from "components/switchButton";
import Input from "components/input";
import Button from "components/button";
import { Link } from "react-router-dom";
import Select from "components/select";
import { get, isEmpty } from "lodash";
import { IPickupAccountDetail, IPickupAccountForm, PickupAccountType, PickupType } from "interfaces";
import { ACCOUNT_PICK_TYPES, PICKUP_ACCOUNT_TYPES } from "utills/constants";
import { useEffect } from "react";

export interface PickupAccountFormProps {
  id?: string;
  defaultData?: IPickupAccountDetail;
  onSubmit: (data: IPickupAccountForm) => void;
}

const PickupAccountSchema = z.discriminatedUnion("role_name", [
  z.object({
    role_name: z.literal(PickupAccountType.POS),
    event_id: z.string().min(1, "This field is required"),
    is_active: z.boolean(),
    username: z.string().min(1, "This field is required").max(50, "Text length is too long"),
    password: z.string().min(1, "This field is required").min(6, "Please enter at least 6 characters").max(50, "Text length is too long"),
    is_editable: z.boolean(),
    name: z.string().min(1, "This field is required").max(50, "Text length is too long"),
    category: z.array(z.string()).min(1, "This field is required"),
    pickup_type: z.string().min(1, "This field is required"),
    is_zone: z.boolean(),
  }),
  z.object({
    role_name: z.literal(PickupAccountType.ADMIN),
    event_id: z.string().min(1, "This field is required"),
    is_active: z.boolean(),
    username: z.string().min(1, "This field is required").max(50, "Text length is too long"),
    password: z.string().min(1, "This field is required").min(6, "Please enter at least 6 characters").max(50, "Text length is too long"),
  })
])

export const PickupAccountForm = ({ onSubmit, id, defaultData }: PickupAccountFormProps) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IPickupAccountForm, HttpError, IPickupAccountForm>({
    defaultValues: {
      category: defaultData?.category ?? [],
      event_id: defaultData?.event_id ?? "",
      is_active: defaultData?.is_active ?? true,
      is_editable: defaultData?.is_editable ?? !id,
      name: defaultData?.name ?? "",
      password: defaultData?.password ?? "",
      pickup_type: defaultData?.pickup_type ?? PickupType.ALL,
      role_name: defaultData?.role_name.toLowerCase() ?? PickupAccountType.POS,
      username: defaultData?.username ?? "",
      is_zone: defaultData?.is_zone ?? false,
    },
    resolver: zodResolver(PickupAccountSchema),
  });

  const { data: eventData, isFetching: isFetchingEvents } = useOne< {id: string, name: string}[], HttpError>({
    resource: "pickup/events/dropdown",
    id: "",
    meta: {
      resource: "data"
    }
  });

  const {
    data: categoryData,
    isFetching: isFetchingCategories,
  } = useOne<string[]>({
    resource: "pickup/events",
    id: `${watch("event_id")}/categories/dropdown`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!watch("event_id"),
    } as any,
  });

  const categoryArr = ((categoryData?.data ?? []).length > 0 ? ["All"] : []).concat((categoryData?.data ?? [])).map((category: string) => (
    { value: category, label: category }
  ));

  const eventIds = (eventData?.data ?? []).map((event: {id: string, name: string}) => (
    { value: event.id, label: event.name }
  ));

  useEffect(() => {
    if (!isEmpty(eventData?.data) && isEmpty(defaultData)) {
      setValue("event_id", (eventData?.data ?? [])[0]?.id ?? "")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData])

  const _submit = (values: IPickupAccountForm) => {
    const submitValues = { ...values };
    onSubmit(submitValues);
  };

  return (
    <div className="w-full ">
      <form onSubmit={handleSubmit(_submit)}>
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <div className="w-full box-container">
            <div className="grid gap-x-4 gap-y-4 sm:grid-cols-6 lg:gap-x-6">
              <div className="sm:col-span-2 flex items-start gap-x-[40px]">
                <div className="">
                  <Label required id="is_active" name="Status" />
                  <Controller
                    control={control}
                    name="is_active"
                    defaultValue={true}
                    render={({ field: { onChange, value = true, ref } }) => (
                      <SwitchButton checked={value} onChange={onChange} />
                    )}
                  />
                  {get(errors, "is_active", "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, `is_active.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>

                {watch("role_name") === PickupAccountType.POS ? (
                  <div className="">
                    <Label required id="is_zone" name="Is Zone" />

                    <Controller
                      control={control}
                      name="is_zone"
                      defaultValue={false}
                      render={({ field, fieldState }) => {
                        return (
                          <SwitchButton
                            disabled={id ? true : false}
                            checked={field?.value}
                            onChange={field?.onChange}
                          />
                        );
                      }}
                    />

                    {get(errors, "is_zone", "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(errors, `is_zone.message`, "")}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <></>
                )}

                {watch("role_name") === PickupAccountType.POS ? (
                  <div className="">
                    <Label required id="is_editable" name="Update Permission" />
                    <Controller
                      control={control}
                      name="is_editable"
                      defaultValue={true}
                      render={({ field: { onChange, value = true, ref } }) => (
                        <SwitchButton checked={value} onChange={onChange} />
                      )}
                    />
                    {get(errors, "is_editable", "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(errors, `is_editable.message`, "")}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ) : <></>}
              </div>
              <div className="sm:col-span-4">
                <Label required id="event_id" name="Event name" />
                <Controller
                  control={control}
                  name="event_id"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key="event_id"
                        inputRef={ref}
                        options={eventIds}
                        isLoading={isFetchingEvents}
                        id="event_id"
                        placeholder="Select event"
                        defaultValue={
                          eventIds.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={eventIds.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                          setValue("category", [])
                        }}
                        error={get(errors, "event_id", "")}
                      />
                    );
                  }}
                />
                {get(errors, "event_id", "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `event_id.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-2">
                <Label required id="role_name" name="Role name" />
                <Controller
                  control={control}
                  name="role_name"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key="role_name"
                        inputRef={ref}
                        options={PICKUP_ACCOUNT_TYPES}
                        isDisabled={id}
                        id="role_name"
                        placeholder="Select role name"
                        defaultValue={
                          PICKUP_ACCOUNT_TYPES.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={PICKUP_ACCOUNT_TYPES.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                        }}
                        error={get(errors, "role_name", "")}
                      />
                    );
                  }}
                />
                {get(errors, "role_name", "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `role_name.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-2">
                <Label required id="username" name="User name" />
                <Input
                  id="username"
                  type="text"
                  maxLength={50}
                  placeholder="User name"
                  error={errors["username"]}
                  {...register("username")}
                />
                <div className="flex justify-between flex-row-reverse">
                  <p className="text-xs leading-6">{(watch("username") ?? "").length}/50</p>
                  {errors["username"] ? ( 
                    <p className="error-text">
                      <span className="font-medium">
                        {errors["username"]?.message}
                      </span>
                    </p> )
                  : null}
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label required id="password" name="Password" />
                <Input
                  id="password"
                  type="text"
                  maxLength={50}
                  placeholder="Password"
                  error={errors["password"]}
                  {...register("password")}
                />
                <div className="flex justify-between flex-row-reverse">
                  <p className="text-xs leading-6">{(watch("password") ?? "").length}/50</p>
                  {errors["password"] ? ( 
                    <p className="error-text">
                      <span className="font-medium">
                        {errors["password"]?.message}
                      </span>
                    </p> )
                  : null}
                </div>
              </div>
              {watch("role_name") === PickupAccountType.POS ? (
                <>
                  <div className="sm:col-span-2">
                    <Label required id="name" name="Account name" />
                    <Input
                      id="name"
                      type="text"
                      maxLength={50}
                      placeholder="Account name"
                      error={errors["name"]}
                      {...register("name")}
                    />
                    <div className="flex justify-between flex-row-reverse">
                      <p className="text-xs leading-6">{(watch("name") ?? "").length}/50</p>
                      {errors["name"] ? ( 
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["name"]?.message}
                          </span>
                        </p> )
                      : null}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <Label required id="category" name="Category" />
                    <Controller
                      control={control}
                      name="category"
                      defaultValue={[]}
                      render={({ field: { onChange, value = [], ref } }) => (
                        <Select
                          isMulti
                          key={`category__${watch("event_id")}`}
                          isLoading={isFetchingCategories}
                          inputRef={ref}
                          options={value.includes("All") ? [] : categoryArr}
                          id="category"
                          placeholder="Select category"
                          value={value.map((id: string) => {
                            return categoryArr.find((item) => item.value === id);
                          })}
                          onChange={(selected: { label: string; value: string }[]) =>
                            selected.find(
                              (item) => item.value === "All"
                            )
                              ? onChange(["All"])
                              : onChange(selected.map((item) => `${item.value}`))
                          }
                          error={get(errors, "category", "")}
                        />
                      )}
                    />
                    {get(errors, "category", "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(errors, `category.message`, "")}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  <div className="sm:col-span-2">
                    <Label required id="pickup_type" name="Pickup Type" />
                    <Controller
                      control={control}
                      name="pickup_type"
                      render={({ field: { onChange, value = "", ref } }) => {
                        return (
                          <Select
                            key="pickup_type"
                            inputRef={ref}
                            options={ACCOUNT_PICK_TYPES}
                            id="pickup_type"
                            placeholder="Select pickup type"
                            defaultValue={
                              ACCOUNT_PICK_TYPES.find(
                                (selected: { label: string; value: any }) =>
                                  selected.value === value
                              ) ?? ""
                            }
                            value={ACCOUNT_PICK_TYPES.find(
                              (selected: { label: string; value: any }) =>
                                selected.value === value
                            )}
                            onChange={(selected: { label: string; value: any }) => {
                              onChange(selected?.value);
                            }}
                            error={get(errors, "pickup_type", "")}
                          />
                        );
                      }}
                    />
                    {get(errors, "pickup_type", "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(errors, `pickup_type.message`, "")}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </>
              ) : <></>}
            </div>
          </div>
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/pickup/accounts${id ? `/${id}` : ""}`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit">
                Save
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
